import styled from '@emotion/styled'
import React from 'react'
import { IDimension, IPieDataType } from '@types'
import { ResponsivePie } from '@nivo/pie'
import { useTheme } from '@ss/meta-core-components'
import { LegendProps } from '@nivo/legends'
import { initTheme, MAIN_PALETTE } from '@utils'

interface Props {
  data: IPieDataType[]
  innerRadius?: number
  startAngle?: number
  endAngle?: number
  dataCy?: string
  dimensions?: IDimension
  showPercentage?: string
}

/**
 * Componente custom per un grafico a torta
 * @param props.data {Array<IGenericObject<string | number>>}
 * @param props.innerRadius {number=} numero da 0 a 1 indica il raggio della circonferenza interna
 * @param props.startAngle {number=} angolo di start del grafico a torta
 * @param props.endAngle {number=} angolo finale del grafico a torta
 * @param props.dimensions {IDimension= } dimensioni del container
 * @param props.dataCy {string=} support test e2e
 * @param props.showPercentage {string=} nome del campo contenente la percentuale del valore
 * @component
 */
export const Pie: React.FC<Props> = (props) => {
  const {
    data,
    innerRadius = 0.5,
    startAngle = 0,
    endAngle = 360,
    dataCy = '',
    dimensions = { height: '375px', width: '100%' },
    showPercentage,
  } = props

  const { type } = useTheme()

  //TODO: controllare la leggenda

  const chartProps = {
    data: [...data],
    margin: { top: 40, right: 80, bottom: 80, left: 80 },
    innerRadius,
    padAngle: 0.7,
    cornerRadius: 3,
    sortByValue: true,
    borderWidth: 1,
    enableArcLinkLabels: false,
    startAngle,
    animate: true,
    endAngle,
    colors: MAIN_PALETTE,
  }
  const legends: LegendProps[] = [
    {
      anchor: 'bottom',
      direction: 'row',
      justify: false,
      translateX: 10,
      translateY: 56,
      itemsSpacing: 5,
      itemWidth: 150,
      itemHeight: 18,
      itemTextColor: '#999',
      itemDirection: 'left-to-right',
      itemOpacity: 0.85,
      symbolSize: 18,
      symbolShape: 'square',
      effects: [
        {
          on: 'hover',
          style: {
            itemOpacity: 1,
          },
        },
      ],
    },
  ]

  return (
    <Container
      height={dimensions.height || '375px'}
      width={dimensions.width || '100%'}
      data-cy={`${dataCy}-pie`}
    >
      <ResponsivePie
        {...chartProps}
        arcLabel={function (d) {
          if (showPercentage) {
            return `${d.data[showPercentage] || d.value}`
          }
          return `${d.value}`
        }}
        borderColor={{
          from: 'color',
          modifiers: [['darker', 0.2]],
        }}
        legends={legends}
        theme={initTheme(type)}
      />
    </Container>
  )
}

const Container = styled.div<{ height: string; width: string }>`
  height: ${({ height }) => height};
  width: ${({ width }) => width};
`
